import { observable, action } from 'mobx';
import notificationStore from './notificationStore';
import fetchDataHelper from '../helpers/fetchDataHelper';
import toastNotificationHelper from '../helpers/toastNotificationHelper';
import scannerHelper from '../helpers/scannerHelper';

export interface IScannerStore {
	actualChanges: IChange[];
	recentChanges: IChange[];
	rerenderFlag: boolean;
	setChanges(changes: any[]): void;
	clearRecentChanges(): Promise<void>;	
}

export interface IChange {
	symbol: string;
	volumeDailyChange: number;
	items: IItem[];
	trades: ITrade[];
	soundIndexToNotify: number;
	title: string;
	browserNotifications: string[];	
}

export interface IItem{
	validFrom: Date;
	filterId: number;
	filterIndex: number;
	filterColorCode: string;
	interval: number;
	priceChangeDirection: number;
	priceChangePercentage: number;
	minPriceChangePercentage: number;
	maxPriceChangePercentage: number;	
	changeTrades: ITrade[];
	volumeIntervalChange: number;
}

export interface ITrade {
	id: number;
	//symbol: string;
	timestamp: Date;
	amount: number;
	price: number;
	usd: number;
}


class ScannerStore implements IScannerStore {

	changes: IChange[] = [];
	actualChangesSymbolsForSort: string[] = [];
	maxValidFromDateActualChanges: Date;
	timer: NodeJS.Timeout;
	@observable rerenderFlag: boolean = false;
	@observable actualChanges: IChange[] = [];  
	@observable recentChanges: IChange[] = [];

	@action.bound setChanges(changes: any[]): void {

		if (this.timer == null) {
			var that = this;
			this.timer = setInterval(action(function tick() {
				that.onSecondElapsed();
			}), 1000);
		}
						
	
		this.changes = this.normalizeChanges(changes);

		this.splitChanges();

		this.actualChanges.forEach(ch => {
			if (ch.soundIndexToNotify > 0){
				notificationStore.playSound(ch.soundIndexToNotify);
			}

			if (ch.title){
				notificationStore.setTitleNotification(ch.title);     	
			}
			
			if (ch.browserNotifications){
				ch.browserNotifications.forEach(bn => {
					notificationStore.setBrowserNotification(bn); 
				})
			}

			ch.items.forEach(i => {
				if (this.maxValidFromDateActualChanges == null || i.validFrom > this.maxValidFromDateActualChanges)
				this.maxValidFromDateActualChanges = i.validFrom;
			});
		})
	}

	normalizeChanges(changes: any[]): IChange[]{
		return changes.map(ch => {
			var minified = ch.s != null;

			return {
				symbol : minified ? ch.s : ch.symbol,
				volumeDailyChange : minified ? ch.d : ch.volumeDailyChange,
				items : this.normalizeChangeItems(minified ? ch.i : ch.items, minified),
				trades : this.normalizeChangeTrades(minified ? ch.t : ch.trades, minified),
				soundIndexToNotify : minified ? ch.o : ch.soundIndexToNotify,
				title : minified ? ch.l : ch.title,
				browserNotifications : minified ? ch.b : ch.browserNotifications	
			};
		});
	}

	normalizeChangeItems(items: any[], minified: boolean): IItem[]{
		return items.map(i => {	
			return {
				validFrom: minified ? i.v : i.validFrom,
				filterId: minified ? i.d : i.filterId,
				filterIndex: minified ? i.x : i.filterIndex,
				filterColorCode: minified ? i.c : i.filterColorCode,
				interval: minified ? i.i : i.interval,
				priceChangeDirection: minified ? i.r : i.priceChangeDirection,
				priceChangePercentage: minified ? i.p : i.priceChangePercentage,
				minPriceChangePercentage: minified ? i.n : i.minPriceChangePercentage,
				maxPriceChangePercentage: minified ? i.a : i.maxPriceChangePercentage,	
				changeTrades: this.normalizeChangeTrades(minified ? i.t : i.changeTrades, minified),
				volumeIntervalChange: minified ? i.e : i.volumeIntervalChange	
			};
		});
	}

	normalizeChangeTrades(trades: any[], minified: boolean): ITrade[]{
		return trades.map(t => {	
			return {
				id: minified ? t.i : t.id,
				timestamp: minified ? t.t : t.timestamp,
				amount: minified ? t.a : t.amount,
				price: minified ? t.p : t.price,
				usd: minified ? t.u : t.usd
			};
		});
	}

	splitChanges(): void {
		var utcDate = (new Date()).getTime();
		var minAgoUtcDate = utcDate - 60000;

		var tmpActualChanges = this.changes
		.map(ch => {
			var clonedChange = {...ch};
			clonedChange.items = ch.items.filter(i => new Date(i.validFrom).getTime() > minAgoUtcDate).map(i => {
				return {...i}
			});
			return clonedChange;
		})
		.filter(ch => ch.items.length > 0);

		this.updateActualChangesSymbolsForSort(tmpActualChanges);

		this.actualChanges = tmpActualChanges
			.sort((ch1, ch2) => this.actualChangesSymbolsForSort.indexOf(ch2.symbol) - this.actualChangesSymbolsForSort.indexOf(ch1.symbol));


		this.recentChanges = this.changes
		.map(ch => {
			var clonedChange = {...ch};
			clonedChange.items = ch.items.filter(i => new Date(i.validFrom).getTime() <= minAgoUtcDate).map(i => {
				return {...i}
			});
			return clonedChange;
		})
		.filter(ch => ch.items.length > 0)
		.sort((ch1, ch2) => scannerHelper.findMaxValidFrom(ch2.items) - scannerHelper.findMaxValidFrom(ch1.items));
	}

	updateActualChangesSymbolsForSort(actualChanges: IChange[]) {
		var actualSymbols = actualChanges.map(ch => ch.symbol);

		// remove not actual symbols
		this.actualChangesSymbolsForSort = this.actualChangesSymbolsForSort.filter(s => actualSymbols.includes(s));

		// add new actual symbols
		actualSymbols.forEach(s => {
			if (!this.actualChangesSymbolsForSort.includes(s))
			{
				this.actualChangesSymbolsForSort.unshift(s);
			}
		})
	}

	onSecondElapsed(): void {

		var utcDate  = new Date();
		
		if (utcDate.getSeconds() % 10 == 0) // re-render UI every 10 seconds to update recent changes timer
		{
			console.log('[scannerStore][onSecondElapsed] 10th second elapsed');

			if ( this.actualChanges.length == 0 ){
				this.rerenderFlag = !this.rerenderFlag;
			} else {
				this.splitChanges();
			}

		}
	}	
	
	@action.bound clearRecentChanges(): Promise<void> {
		return fetchDataHelper.post("/scanner/clearChanges", null)   
			.then(
				() => {
					return Promise.resolve();
				},
				(error) => {
					toastNotificationHelper.notifyError('Clear changes failed');
					console.log('Clear changes failed: ' + error);
					return Promise.reject();
				}
			);
	}  
}

export default new ScannerStore();